import { useCallback, useMemo } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import {
  mapRelayCurrencyToFeaturedToken,
  RelayCurrencySearchResult,
  RelayFeaturedToken,
} from '~/hooks/useRelayNetworks'
import { VmType } from '~/types'
import { Address } from '~/utils/address'
import { captureException } from '~/utils/errors'

type UseRelayCurrencySearchParams = {
  chainIds: number[] | undefined
  searchTerm: string
  enabled: boolean
  vmTypes?: VmType[]
  /** When true, sends chainIds to the API and includes them in the query key
   *  so the fetch is scoped to those chains. Use when a relay chain is selected
   *  whose tokens won't appear in the broad (unscoped) search. */
  scopeToChains?: boolean
}

export type RelayCurrencySearchResultWithChainId = RelayFeaturedToken & {
  chainId: number
}

// Relay tags some non-EVM tokens (e.g. Move coin types) as `evm` and returns an
// address only their own VM can parse. Formatting one throws, and since the
// batch is mapped as a unit that single row rejects every other result.
const hasParseableAddress = (result: RelayCurrencySearchResult): boolean =>
  Address.validate(
    result.address,
    Address.supportedTypes(result.vmType as VmType),
  )

// Relay can return multiple contracts with the same symbol on a chain.
// Prefer verified entries when they exist, but keep all results otherwise.
// Address-based searches are handled separately and bypass this filter.
const filterRelaySearchResults = (
  supportedVmTypes: VmType[],
  results: RelayCurrencySearchResult[],
): RelayCurrencySearchResult[] => {
  const supported = results.filter((result) =>
    supportedVmTypes.includes(result.vmType as VmType),
  )
  const grouped = supported.reduce<Record<string, RelayCurrencySearchResult[]>>(
    (acc, result) => {
      const key = `${result.chainId}-${result.symbol.toLowerCase()}`
      acc[key] = [...(acc[key] ?? []), result]
      return acc
    },
    {},
  )

  return Object.values(grouped).flatMap((group) => {
    const verified = group.filter((token) => token.metadata?.verified)
    return verified.length > 0 ? verified : group
  })
}

/**
 * Hook to search for tokens on Relay networks using their currency search API
 * Supports both symbol/name search and address-based search
 */
export const useRelayCurrencySearch = ({
  chainIds,
  searchTerm,
  enabled,
  vmTypes = ['evm'],
  scopeToChains = false,
}: UseRelayCurrencySearchParams) => {
  const queryClient = useQueryClient()

  // Stable key from array contents so memoization works across re-renders
  const chainIdsKey = chainIds?.slice().sort().join(',')
  const chainIdSet = useMemo(
    () => (chainIds ? new Set(chainIds) : undefined),
    [chainIdsKey], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Client-side filter so switching between org chain tabs is instant (shared cache).
  const selectFn = useCallback(
    (data: RelayCurrencySearchResultWithChainId[]) =>
      chainIdSet ? data.filter((token) => chainIdSet.has(token.chainId)) : data,
    [chainIdSet],
  )

  return useQuery<RelayCurrencySearchResultWithChainId[]>({
    // When scopeToChains is true (relay chain selected), include chainIds in
    // the key so relay-scoped fetches get their own cache entry. Otherwise
    // org-chain switches reuse the broad cache filtered client-side via select.
    queryKey: [
      QUERY_KEYS.RELAY_CURRENCY_SEARCH,
      searchTerm,
      vmTypes,
      scopeToChains ? chainIdsKey : undefined,
    ],
    queryFn: async () => {
      // Don't pass `verified: true` - we want to show all tokens including
      // unverified ones to give users more search flexibility. The
      // filterRelaySearchResults function still prefers verified tokens
      // when duplicates exist.
      const requestBody = {
        ...(Address.validate(searchTerm)
          ? { address: searchTerm }
          : { term: searchTerm }),
        ...(scopeToChains && chainIds ? { chainIds } : {}),
        limit: 20,
        // External search breaks results for some relay chains (e.g. 69000),
        // so disable it when scoping to specific chains.
        useExternalSearch: !scopeToChains,
      }

      const response = await fetch('https://api.relay.link/currencies/v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        // Log the error but return empty results instead of throwing.
        // This prevents the UI from showing an error state for search failures.
        captureException(new Error('Failed to fetch relay currencies'))
        return []
      }

      const data = (await response.json()) as RelayCurrencySearchResult[]

      const parseableData = data.filter(hasParseableAddress)

      const filteredData = Address.validate(searchTerm)
        ? parseableData
        : filterRelaySearchResults(vmTypes, parseableData)
      const mappedTokens = filteredData.map(
        (currency): RelayCurrencySearchResultWithChainId => ({
          ...mapRelayCurrencyToFeaturedToken(currency),
          chainId: currency.chainId,
        }),
      )

      // Pre-populate TOKEN_METADATA cache so TokenDisplay renders without additional fetches
      mappedTokens.forEach((token) => {
        const logoUri = token.metadata.logoURI?.startsWith('http')
          ? token.metadata.logoURI
          : undefined
        queryClient.setQueryData(
          [
            QUERY_KEYS.TOKEN_METADATA,
            token.chainId,
            Address.format(token.address),
          ],
          {
            address: token.address,
            symbol: token.symbol,
            decimals: token.decimals,
            validToken: true,
            logoUri,
            isPosition: false,
            chainId: token.chainId,
          },
        )
      })

      return mappedTokens
    },
    select: selectFn,
    enabled:
      enabled &&
      (chainIds === undefined || chainIds.length > 0) &&
      searchTerm.length > 0,
    staleTime: 1000 * 60 * 20,
  })
}
